/**
 * Accessibility Alerts Fix
 * Addresses remaining WAVE accessibility alerts
 */

(function() {
  'use strict';

  /**
   * Fix redundant links - consolidate multiple links to same destination
   */
  function fixRedundantLinks() {
    const links = document.querySelectorAll('a[href]');
    const linkMap = new Map();
    
    links.forEach(link => {
      const href = link.getAttribute('href');
      if (!href || href === '#' || href.startsWith('javascript:')) return;
      
      if (!linkMap.has(href)) {
        linkMap.set(href, []);
      }
      linkMap.get(href).push(link);
    });
    
    // For links that point to the same destination and are adjacent or in the same container,
    // we can consolidate them or add aria-label to distinguish them
    linkMap.forEach((linkArray, href) => {
      if (linkArray.length > 1) {
        // Check if links are in the same parent container
        const parentContainers = new Set();
        linkArray.forEach(link => {
          const parent = link.closest('article, .product-item, .collection-item, .post-item');
          if (parent) {
            parentContainers.add(parent);
          }
        });
        
        // If multiple links in same container, mark subsequent ones as redundant
        parentContainers.forEach(container => {
          const containerLinks = linkArray.filter(link => 
            container.contains(link) && link.getAttribute('href') === href
          );
          
          if (containerLinks.length > 1) {
            // Keep first link, mark others with aria-label indicating redundancy
            for (let i = 1; i < containerLinks.length; i++) {
              const link = containerLinks[i];
              if (!link.getAttribute('aria-label')) {
                const firstLinkText = containerLinks[0].textContent.trim() || 
                                     containerLinks[0].getAttribute('aria-label') || 
                                     'Link';
                link.setAttribute('aria-label', `Alternative link to ${firstLinkText}`);
              }
            }
          }
        });
      }
    });
  }

  /**
   * Fix very small text - ensure minimum readable size
   */
  function fixVerySmallText() {
    const allElements = document.querySelectorAll('*');
    allElements.forEach(element => {
      const computedStyle = window.getComputedStyle(element);
      const fontSize = parseFloat(computedStyle.fontSize);
      
      // If font size is less than 12px, increase it
      if (fontSize > 0 && fontSize < 12) {
        element.style.fontSize = '14px';
        element.style.lineHeight = '1.5';
      }
    });
  }

  /**
   * Fix skipped heading levels
   */
  function fixSkippedHeadingLevels() {
    const headings = Array.from(document.querySelectorAll('h1, h2, h3, h4, h5, h6'));
    let lastLevel = 0;
    
    headings.forEach(heading => {
      const level = parseInt(heading.tagName.charAt(1));
      
      // If we skip more than one level, adjust the heading
      if (lastLevel > 0 && level > lastLevel + 1) {
        const newLevel = lastLevel + 1;
        const newTag = `h${newLevel}`;
        const newHeading = document.createElement(newTag);
        
        // Copy attributes
        Array.from(heading.attributes).forEach(attr => {
          newHeading.setAttribute(attr.name, attr.value);
        });
        
        // Copy content
        newHeading.innerHTML = heading.innerHTML;
        
        // Replace
        heading.parentNode.replaceChild(newHeading, heading);
        lastLevel = newLevel;
      } else {
        lastLevel = level;
      }
    });
  }

  /**
   * Fix suspicious link text (links with generic text like "click here", "read more")
   */
  function fixSuspiciousLinkText() {
    const suspiciousPatterns = [
      /^(click here|read more|here|link|more|continue|next|previous)$/i,
      /^(click|link|more|here)$/i
    ];
    
    const links = document.querySelectorAll('a[href]');
    links.forEach(link => {
      const text = link.textContent.trim();
      
      if (suspiciousPatterns.some(pattern => pattern.test(text))) {
        // Try to get context from surrounding elements
        const parent = link.parentElement;
        const heading = parent ? parent.querySelector('h1, h2, h3, h4, h5, h6') : null;
        const article = link.closest('article');
        const articleTitle = article ? article.querySelector('h1, h2, h3, h4, h5, h6') : null;
        
        let betterText = text;
        if (heading) {
          betterText = `${text} about ${heading.textContent.trim()}`;
        } else if (articleTitle) {
          betterText = `${text} about ${articleTitle.textContent.trim()}`;
        } else {
          // Get href and create descriptive text
          const href = link.getAttribute('href');
          if (href && href !== '#') {
            const urlParts = href.split('/').filter(p => p);
            const lastPart = urlParts[urlParts.length - 1];
            if (lastPart) {
              betterText = `${text} - ${lastPart.replace(/[-_]/g, ' ')}`;
            }
          }
        }
        
        // Update aria-label if text is still generic
        if (betterText !== text) {
          link.setAttribute('aria-label', betterText);
        }
      }
    });
  }

  /**
   * Review and fix long alternative text
   */
  function fixLongAltText() {
    const images = document.querySelectorAll('img[alt]');
    const maxAltLength = 125; // WCAG recommends keeping alt text concise
    
    images.forEach(img => {
      const alt = img.getAttribute('alt');
      if (alt && alt.length > maxAltLength) {
        // Truncate but keep it meaningful
        const truncated = alt.substring(0, maxAltLength - 3) + '...';
        img.setAttribute('alt', truncated);
        // Also add a title for full description if needed
        if (!img.getAttribute('title')) {
          img.setAttribute('title', alt);
        }
      }
    });
  }

  /**
   * Initialize fixes when DOM is ready
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', function() {
        fixRedundantLinks();
        fixVerySmallText();
        fixSkippedHeadingLevels();
        fixSuspiciousLinkText();
        fixLongAltText();
      });
    } else {
      fixRedundantLinks();
      fixVerySmallText();
      fixSkippedHeadingLevels();
      fixSuspiciousLinkText();
      fixLongAltText();
    }
    
    // Also run after dynamic content loads
    if (typeof MutationObserver !== 'undefined') {
      const observer = new MutationObserver(function(mutations) {
        let shouldFix = false;
        mutations.forEach(function(mutation) {
          if (mutation.addedNodes.length > 0) {
            shouldFix = true;
          }
        });
        if (shouldFix) {
          setTimeout(function() {
            fixRedundantLinks();
            fixVerySmallText();
            fixSkippedHeadingLevels();
            fixSuspiciousLinkText();
            fixLongAltText();
          }, 100);
        }
      });
      
      observer.observe(document.body, {
        childList: true,
        subtree: true
      });
    }
  }

  // Initialize
  init();
})();

